import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, Mail, CheckCircle, Loader } from 'lucide-react';

/**
 * Email verification banner component
 * Shows a banner when email is not verified and allows resending verification email
 */
const EmailVerification = () => {
  const { currentUser, sendVerificationEmail, reloadUser } = useAuth();
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Debug logging removed for privacy

  // Don't show banner if email is verified or user not logged in
  if (!currentUser || currentUser.emailVerified) {
    return null;
  }

  const handleResendEmail = async () => {
    setSending(true);
    setError('');
    setMessage('');

    try {
      await sendVerificationEmail();
      setMessage(
        'Verification email sent! Please check your inbox and spam folder.'
      );
    } catch (err) {
      console.error('Error sending verification email:', err);
      if (err.message.includes('too-many-requests')) {
        setError(
          'Too many requests. Please wait a few minutes before trying again.'
        );
      } else if (err.message.includes('already verified')) {
        setError('Email is already verified.');
      } else {
        setError('Failed to send verification email. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  const handleCheckVerification = async () => {
    setChecking(true);
    setError('');
    setMessage('');

    try {
      const isVerified = await reloadUser();
      if (isVerified) {
        setMessage('Email verified successfully!');
        // Banner will disappear after state update
      } else {
        setMessage(
          'Email not yet verified. Please check your inbox (and spam folder) and click the verification link.'
        );
      }
    } catch (err) {
      console.error('Error checking verification:', err);
      setError('Failed to check verification status.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <AlertTriangle className="h-5 w-5 text-yellow-400" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-yellow-800">
            Email Verification Required
          </h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p className="mb-2">
              Please verify your email address ({currentUser.email}) to access
              all features. Check your inbox for the verification link.
            </p>
            <p className="font-semibold flex items-center gap-1 text-yellow-800">
              <span className="text-lg">📧</span>
              Check your spam/junk folder if you don&apos;t see the email!
            </p>
          </div>

          {/* Message display */}
          {message && (
            <div className="mt-3 text-sm text-green-700 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              {message}
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="mt-3 text-sm text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {error}
            </div>
          )}

          {/* Action buttons */}
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={handleCheckVerification}
              disabled={checking}
              className="inline-flex items-center gap-2 px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {checking ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  I&apos;ve Verified
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleResendEmail}
              disabled={sending}
              className="inline-flex items-center gap-2 px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4" />
                  Resend Email
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
